import type { RouteRecordRaw } from 'vue-router'

import { keys } from '@/app/i18n'
import { notFoundLocation } from '@/app/router/not-found'
import { EMPLOYER_SOFTWARES } from '@/app/stores/session'

import { setOrganizationCrumbs } from './breadcrumb'

/**
 * The company module's routes (WP-06, wp/company §3). Paths and names from the route manifest (D-15).
 *
 * - `organizations` and the two profile routes need the Organization profile read permission (D-21; the API's rule,
 *   FX-057). The profile of the current workspace keeps the legacy `/:software/company-profile/:tab?` (invoices and
 *   quotes deep-link `/ac/company-profile/general-terms`); a software outside the five employer ones is the 404 page,
 *   as on settings. An unknown `:tab` shows the first tab and leaves the URL alone (legacy). While the current
 *   workspace is not set up the page is the setup wizard, framed by the onboarding layout (`meta.onboarding`).
 * - another organisation's profile is the manifest's new `/organizations/:orgId` (OPEN-1 (a): nothing shadows it any
 *   more — NEW-1), its tabs `/organizations/:orgId/:tab` (NEW-2). Its breadcrumb "Company Profile" links the session's
 *   software profile (legacy `currentSoftware` at load time).
 * - the three public pages keep their exact paths (D-15), render without the shell and need no session.
 */
const PROFILE_TITLE = keys.t('company.profile.title.organizationProfile')

const routes: RouteRecordRaw[] = [
  {
    path: '/organizations',
    name: 'organizations',
    component: () => import('./views/OrganizationsView.vue'),
    meta: {
      resource: 'company-profile',
      pageTitle: keys.t('company.organizations.title.yourOrganizations'),
      breadcrumb: [{ text: keys.t('company.organizations.title.yourOrganizations'), active: true }],
    },
  },
  {
    path: '/:software/company-profile/:tab?',
    name: 'company-profile',
    component: () => import('./views/CompanyProfileView.vue'),
    beforeEnter: (to) =>
      (EMPLOYER_SOFTWARES as readonly string[]).includes(String(to.params.software)) ? true : notFoundLocation(to),
    meta: {
      software: 'common',
      resource: 'company-profile',
      onboarding: true,
      pageTitle: PROFILE_TITLE,
      breadcrumb: [{ text: PROFILE_TITLE, active: true }],
    },
  },
  {
    path: '/organizations/:orgId(\\d+)/:tab?',
    name: 'company-profile-id',
    component: () => import('./views/CompanyProfileView.vue'),
    beforeEnter: (to) => {
      setOrganizationCrumbs(to)
      return true
    },
    meta: {
      resource: 'company-profile',
      pageTitle: PROFILE_TITLE,
    },
  },
  {
    path: '/apply/:id',
    name: 'apply',
    component: () => import('./views/ApplyView.vue'),
    meta: { layout: 'full', public: true },
  },
  {
    path: '/resume/:id',
    name: 'resume',
    component: () => import('./views/ResumeView.vue'),
    meta: { layout: 'full', public: true, pageTitle: keys.t('company.resume.title.resume') },
  },
  {
    path: '/company/:id',
    name: 'company',
    component: () => import('./views/CareerPageView.vue'),
    meta: { layout: 'full', public: true, pageTitle: keys.t('company.career.title.company') },
  },
]

export default routes
